import { Controller } from "@hotwired/stimulus"

// "Turn the slide around" (card #2079 Phase 4, redesigned to a 3-way flip
// after Jay's first round of live feedback; card #2514 changed Notes/Feedback
// from a hide-the-front swap to an animated grow-open beneath it) — switches
// a show-page slide between its normal artwork (front), a read-only
// standing-note view (notes), and an attributed, appendable comment thread +
// review-decision panel (feedback). Declared on the SAME wrapper as
// copy-slide (_viewer_chrome.html.erb), one instance per slide — in-memory
// only, resets to slide-view on reload (this is per-slide-per-visit state,
// not a standing preference, so no localStorage persistence like
// objective_view_toggle_controller.js).
export default class extends Controller {
  static targets = [
    "front", "notes", "feedback",
    "slideSegment", "notesSegment", "feedbackSegment",
    "statusPill"
  ]
  static values = { slideUuid: String }

  // CARD #2536: the retired feedback-summary-list's "Go to slide →" link used
  // to dispatch a "deck:show-feedback" document event here (via the now-
  // deleted feedback_filter_controller.js#goToSlideFeedback), which connect()
  // listened for to auto-open this panel. That whole mechanism is gone —
  // airdrop_report_viewer_controller.js now opens a matching slide's Feedback
  // panel directly via Stimulus's own controller registry
  // (application.getControllerForElementAndIdentifier(...).showPanel("feedback")),
  // calling showPanel below with no event indirection needed at all.

  // The 3-way segmented toggle's action, AND the toolbar's status pill
  // (_slide_status_pill.html.erb, always panel: "feedback") — event.params.panel
  // is one of "front" | "notes" | "feedback" (data-deck-slide-flip-panel-param
  // on each segment button / the status pill).
  show(event) {
    this.showPanel(event.params.panel)
  }

  // The front slide is never hidden — it stays visible at all times. Only
  // notes/feedback toggle open/closed, via `is-open` (the CSS grid-rows
  // collapse/expand lives in application.css) rather than Tailwind's `hidden`,
  // so the panel grows open beneath the still-visible slide instead of
  // snapping the layout the instant either is selected.
  showPanel(panel) {
    if (!this.hasFrontTarget) return

    const panels = {
      notes: this.hasNotesTarget ? this.notesTarget : null,
      feedback: this.hasFeedbackTarget ? this.feedbackTarget : null
    }

    Object.entries(panels).forEach(([name, el]) => {
      if (el) el.classList.toggle("is-open", name === panel)
    })

    this.updateSegments(panel)
    this.updateStatusPill(panel)
  }

  // Jay flagged (screenshot) that the toolbar's status pill and the
  // Feedback panel's own status banner (_slide_review_actions) were both
  // visible at once when flipped to Feedback — fully redundant, since the
  // banner already shows status + who + when. Hide the pill ONLY while
  // Feedback is the active panel; Slide/Notes still need it as the sole
  // status indicator, still clickable to jump to Feedback.
  //
  // Uses `invisible` (visibility:hidden), NOT `hidden` (display:none). Jay
  // flagged a second time (screenshot) that `hidden` collapsed the pill's
  // flex slot entirely, so the toolbar's `justify-between` row
  // (_slide_toolbar.html.erb) redistributed the remaining two items and the
  // Slide/Notes/Feedback segmented toggle visibly shifted left. `invisible`
  // keeps the pill's box in the layout — same width/position, just not
  // rendered — so the toggle never moves.
  updateStatusPill(panel) {
    if (!this.hasStatusPillTarget) return

    this.statusPillTarget.classList.toggle("invisible", panel === "feedback")
  }

  updateSegments(panel) {
    const segments = {
      front: this.hasSlideSegmentTarget ? this.slideSegmentTarget : null,
      notes: this.hasNotesSegmentTarget ? this.notesSegmentTarget : null,
      feedback: this.hasFeedbackSegmentTarget ? this.feedbackSegmentTarget : null
    }
    const activeClasses = ["bg-gray-100", "text-gray-900"]
    const inactiveClasses = ["text-gray-400", "hover:text-gray-600"]

    Object.entries(segments).forEach(([name, el]) => {
      if (!el) return

      if (name === panel) {
        el.classList.add(...activeClasses)
        el.classList.remove(...inactiveClasses)
      } else {
        el.classList.remove(...activeClasses)
        el.classList.add(...inactiveClasses)
      }
    })
  }
}
